import React, { Fragment, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { Input, useIsMobileResolution } from '@philips-internal/oswald-ui-components';
import { Placement } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, expression, func, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, isFunction, trigger } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as CancelIcon } from '../../../resources/assets/cancel-md-dark.svg';
import { ReactComponent as MenuIcon } from '../../../resources/assets/DLS_Menu.svg';
import { ReactComponent as NavigationIcon } from '../../../resources/assets/DLS_Navigation.svg';
import { ReactComponent as MagnifierIcon } from '../../../resources/assets/magnifier.svg';
import { isApplicationModule, isApplicationModuleGroup } from '../../application-module-registry.js';
import { applicationContext, foundationContext } from '../../context/index.js';
import { SPOTLIGHT_PARENT_SLIDEBAR_SELECTOR } from '../spotlight/index.js';
import { ModuleSelectorAPI } from './module-selector-api.js';
import { ModuleSelectorType } from './module-selector-constants.js';
import ModuleSelectorSpatialDesktop from './module-selector-spatial-desktop.js';
import { flattenAllModules, getAvailableModules, searchInModules } from './module-selector-utils.js';
import './module-selector.css';
const MODULES_PER_PAGE = 12;
const awesomeProps = {
    api: ModuleSelectorAPI,
    type: ComponentType.DATA_DISPLAY,
};
const spliceIntoPages = (modules) => {
    const modulesTemp = [...modules];
    const pages = [];
    while (modulesTemp.length) {
        pages.push(modulesTemp.splice(0, MODULES_PER_PAGE));
    }
    return pages;
};
const createSearchIntersectionObserver = (searchModifierClassName) => {
    const intersectionCallback = ([entry]) => {
        const { target } = entry;
        target.classList.toggle(searchModifierClassName, entry.intersectionRatio < 1);
    };
    const options = {
        threshold: [1],
    };
    return new IntersectionObserver(intersectionCallback, options);
};
const ensureModulesVisibility = debounce((moduleModifierClassName, threshold) => {
    const moduleElements = document.querySelectorAll('.c-module-selector__module');
    moduleElements.forEach(module => {
        const { x, y, height } = module.getBoundingClientRect();
        const element = document.elementFromPoint(x, y + height - (height * threshold));
        const isBehindSearchOrOutOfScreen = element !== module;
        module.classList.toggle(moduleModifierClassName, isBehindSearchOrOutOfScreen);
    });
}, 50);
const createModulesIntersectionObserver = (moduleModifierClassName) => {
    const slidebarHeaderHeight = document.querySelector('.c-application-bar__spotlight-slidebar .c-slidebar-header')?.clientHeight ?? 0;
    const searchInputHeight = document.querySelector('.c-module-selector__search')?.clientHeight ?? 0;
    const offsetHeight = searchInputHeight + slidebarHeaderHeight;
    const threshold = 0.5;
    const intersectionCallback = ([entry]) => {
        const { target } = entry;
        target.classList.toggle(moduleModifierClassName, entry.intersectionRatio < threshold && entry.boundingClientRect.y < offsetHeight);
        ensureModulesVisibility(moduleModifierClassName, threshold);
    };
    const options = {
        rootMargin: `-${offsetHeight}px 0px 0px`,
        threshold: [threshold],
    };
    return new IntersectionObserver(intersectionCallback, options);
};
const observerSearch = (searchIntersectionObserver) => {
    const searchElement = document.querySelector('.c-module-selector__search');
    if (!searchElement || !searchIntersectionObserver) {
        return;
    }
    searchIntersectionObserver.observe(searchElement);
};
const observerModules = (searchIntersectionObserver) => {
    const moduleElements = document.querySelectorAll('.c-module-selector__module');
    if (!moduleElements.length || !searchIntersectionObserver) {
        return;
    }
    moduleElements.forEach(moduleElement => searchIntersectionObserver.observe(moduleElement));
};
const renderModulesLinearSearch = (options) => {
    const { modules, onModuleClick, title } = options;
    return (modules.map((module, index) => {
        const className = classNames({
            'c-module-selector__module': true,
            'c-module-selector__module--disabled': isApplicationModule(module) && Boolean(module.disabled),
            'c-module-selector__module--search': true,
        });
        return (React.createElement("div", { className: className, key: `${module.id}-${index}`, onClick: trigger(onModuleClick, module) },
            React.createElement("div", { className: "c-module-selector__module-wrapper" },
                React.createElement("div", { className: "c-module-selector__module-title" }, title),
                React.createElement("div", { className: "c-module-selector__module-name" }, module.prettyName))));
    }));
};
const renderModules = (options) => {
    const { modules, onModuleClick, searchTerm } = options;
    return (React.createElement("div", { className: "c-module-selector__modules" }, modules.map((module, index) => {
        const className = classNames({
            'c-module-selector__module': true,
            'c-module-selector__module--disabled': isApplicationModule(module) && Boolean(module.disabled),
        });
        const moduleItem = (React.createElement("div", { className: className, onClick: trigger(onModuleClick, module) },
            React.createElement("div", { className: "c-module-selector__module-name" }, module.prettyName),
            isApplicationModuleGroup(module) && module.modules.length
                ? React.createElement(NavigationIcon, { className: "c-module-selector__module-group-navigation" })
                : null));
        return (React.createElement("div", { key: `${module.id}-${index}` }, (searchTerm && isApplicationModuleGroup(module))
            ? renderModulesLinearSearch({ modules: module.modules, onModuleClick, title: module.prettyName })
            : moduleItem));
    })));
};
const renderSearch = (options) => {
    const { onClearSearch, onSearchChange, searchTerm } = options;
    if (foundationContext.getHideModuleSearch()) {
        return null;
    }
    const searchExpression = foundationContext.getModuleSearchExpression();
    let externalAction = React.createElement(MagnifierIcon, { className: "c-module-selector__search-icon" });
    let externalActionPlacement = Placement.LEFT;
    if (searchTerm) {
        externalAction = React.createElement(CancelIcon, { className: "c-module-selector__search-icon", onClick: onClearSearch });
        externalActionPlacement = Placement.RIGHT;
    }
    return (React.createElement("div", { className: "c-module-selector__section c-module-selector__search" },
        React.createElement(Input, { className: "c-module-selector__search-input", externalAction: externalAction, externalActionPlacement: externalActionPlacement, onChange: onSearchChange, placeholder: searchExpression, role: "searchbox", value: searchTerm })));
};
const renderApplicationTitle = (props) => {
    if (!isFunction(props.onClose)) {
        throw new Error('[ModuleSelector]: Missing onClose event for Linear type of Menu.');
    }
    return (React.createElement("div", { className: "c-module-selector__application-title-wrapper" },
        React.createElement(MenuIcon, { className: "c-module-selector__title-icon", onClick: props.onClose }),
        React.createElement("div", { className: "c-module-selector__title" }, MessageService.translate(props.title ?? ''))));
};
const renderModulesAndTitle = (options) => {
    const { props, onModuleClick, onModuleGroupBackClick, searchTerm, selectedModuleGroup } = options;
    if (!selectedModuleGroup) {
        return null;
    }
    const shouldRenderIcon = isFunction(onModuleGroupBackClick);
    return (React.createElement(Fragment, null,
        React.createElement("div", { className: "c-module-selector__title-wrapper" },
            shouldRenderIcon
                ? React.createElement(NavigationIcon, { className: "c-module-selector__title-icon c-module-selector__nav-icon", onClick: onModuleGroupBackClick })
                : null,
            React.createElement("div", { className: "c-module-selector__title" }, selectedModuleGroup.prettyName),
            shouldRenderIcon && props.onClose
                ? React.createElement(CancelIcon, { className: "c-module-selector__title-icon c-module-selector__close-icon", onClick: props.onClose })
                : null),
        renderModules({ modules: selectedModuleGroup.modules, onModuleClick, searchTerm })));
};
const renderInnerModule = (options) => {
    const { modules, onClearSearch, onModuleClick, onSearchChange, searchTerm } = options;
    return (React.createElement(Fragment, null,
        renderSearch({ onClearSearch, onSearchChange, searchTerm }),
        renderModules({ modules, onModuleClick, searchTerm })));
};
function moduleSelectorReducer(data, partialData) {
    return {
        ...data,
        ...partialData,
    };
}
const reducerInitialState = {
    groups: [],
    modules: [],
    pages: [],
    searchingPages: [],
    spatialDesktop: false,
};
function ModuleSelector(props) {
    const availableModules = useRef([]);
    const availableGroups = useRef([]);
    const searchIntersectionObserver = useRef();
    const modulesIntersectionObserver = useRef();
    const searchModifierClassName = 'c-module-selector__search--top';
    const moduleModifierClassName = 'c-module-selector__module--hidden';
    const [reducerState, setReducerState] = useReducer(moduleSelectorReducer, reducerInitialState);
    const [searchTerm, setSearchTerm] = useState('');
    const isMobileResolution = useIsMobileResolution();
    const moduleSelectorRef = useRef(null);
    const [hasSlidebarAsParent, setHasSlidebarAsParent] = useState(false);
    useLayoutEffect(() => {
        if (!moduleSelectorRef.current) {
            return;
        }
        const slidebar = moduleSelectorRef.current.closest(SPOTLIGHT_PARENT_SLIDEBAR_SELECTOR);
        setHasSlidebarAsParent(Boolean(slidebar?.contains(moduleSelectorRef.current)));
    }, [moduleSelectorRef.current]);
    useLayoutEffect(() => {
        const { availableGroups: groups, availableModules: modules } = getAvailableModules();
        availableModules.current = modules;
        availableGroups.current = groups;
        const pages = spliceIntoPages(modules);
        const spatialDesktop = props.moduleType === ModuleSelectorType.SPOTLIGHT && !isMobileResolution && !hasSlidebarAsParent;
        setReducerState({ groups, modules, pages, spatialDesktop });
    }, [isMobileResolution, hasSlidebarAsParent]);
    useEffect(() => {
        if (props.moduleType === ModuleSelectorType.SPOTLIGHT) {
            searchIntersectionObserver.current = createSearchIntersectionObserver(searchModifierClassName);
            observerSearch(searchIntersectionObserver.current);
        }
        return () => {
            searchIntersectionObserver.current?.disconnect();
        };
    }, [reducerState.spatialDesktop]);
    useEffect(() => {
        if (props.moduleType === ModuleSelectorType.SPOTLIGHT) {
            modulesIntersectionObserver.current = createModulesIntersectionObserver(moduleModifierClassName);
            observerModules(modulesIntersectionObserver.current);
        }
        return () => {
            modulesIntersectionObserver.current?.disconnect();
        };
    }, [reducerState.spatialDesktop, reducerState.modules]);
    useEffect(() => {
        $search();
    }, [searchTerm]);
    const searchApplication = () => {
        if (searchTerm.length <= 0) {
            return setReducerState({ modules: availableModules.current });
        }
        const foundModules = searchInModules(searchTerm, { availableGroups: availableGroups.current, availableModules: availableModules.current });
        const newReducerState = {
            modules: foundModules,
        };
        if (reducerState.spatialDesktop) {
            const allFoundModules = flattenAllModules(foundModules);
            newReducerState.searchingPages = spliceIntoPages(allFoundModules);
        }
        setReducerState(newReducerState);
    };
    const $search = debounce(searchApplication, 300);
    const onModuleClick = (module) => {
        if (isApplicationModuleGroup(module)) {
            return setReducerState({ selectedModuleGroup: module });
        }
        if (module.disabled) {
            return;
        }
        applicationContext.openModule(module.id);
    };
    const onModuleGroupBackClick = () => {
        return setReducerState({ selectedModuleGroup: undefined });
    };
    const onSearchChange = (event) => {
        setSearchTerm(event.value);
    };
    const onClearSearch = () => {
        setSearchTerm('');
    };
    if (reducerState.spatialDesktop) {
        return (React.createElement(ModuleSelectorSpatialDesktop, { onClearSearch: onClearSearch, onModuleClick: onModuleClick, onModuleGroupBackClick: onModuleGroupBackClick, onSearchChange: onSearchChange, pages: searchTerm ? reducerState.searchingPages : reducerState.pages, searchTerm: searchTerm, selectedModuleGroup: reducerState.selectedModuleGroup, showIconsOnSpatialDesktop: Boolean(props.showIconsOnSpatialDesktop) }));
    }
    const modulesAndTitle = renderModulesAndTitle({ onModuleClick, onModuleGroupBackClick, props, searchTerm, selectedModuleGroup: reducerState.selectedModuleGroup });
    const innerModule = renderInnerModule({ modules: reducerState.modules, onClearSearch, onModuleClick, onSearchChange, searchTerm });
    if (props.moduleType === ModuleSelectorType.LINEAR) {
        return (React.createElement("div", { className: "c-module-selector--linear-background", ref: moduleSelectorRef },
            React.createElement("div", { className: "c-module-selector--linear" },
                renderApplicationTitle(props),
                React.createElement("div", { className: "c-module-selector-modules-wrapper u-scrollbar" }, modulesAndTitle ?? innerModule))));
    }
    if (reducerState.selectedModuleGroup) {
        return (React.createElement("div", { className: "c-module-selector__submenu", ref: moduleSelectorRef }, modulesAndTitle));
    }
    return (React.createElement("div", { className: "c-module-selector--default", ref: moduleSelectorRef }, innerModule));
}
ModuleSelector.propTypes = {
    moduleType: string.notEnumerable(),
    onClose: func.notEnumerable(),
    showIconsOnSpatialDesktop: bool,
    title: expression.notEnumerable(),
};
ModuleSelector.defaultProps = {
    moduleType: ModuleSelectorType.SPOTLIGHT,
    searchExpression: 'tws.module-selector.search',
    showIconsOnSpatialDesktop: true,
};
AwesomeComponent(awesomeProps)(ModuleSelector);
export { ModuleSelector, };
//# sourceMappingURL=module-selector.js.map